import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { FileText, MessageSquare, CalendarDays, Quote, Image as ImageIcon, Clock, Rocket, Sparkles, OctagonAlert } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import AiInsightsCard from "@/admin/AiInsightsCard";
import AcademyProUpsell from "@/admin/AcademyProUpsell";
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import { payloadShapeError } from "../hooks/useAdminLoad";
import { adminHref } from "./adminPath";
import { useEntitlements } from "./entitlements/EntitlementsContext";
import { CAP } from "./entitlements/capabilities";

/**
 * ONE CARD = ONE REQUEST = ONE FACT (BUG-056).
 *
 * The six summary reads used to be one `Promise.all` feeding one `stats` object. That
 * makes six independent facts share a single fate: `Promise.all` rejects the instant ANY
 * one request does, `setStats` never runs, `stats` stays at its all-zero initial value,
 * and all six cards report "0" — six claims about the practice, five of them contradicted
 * by data that arrived successfully. The only trace was a `console.error`.
 *
 * Each source now owns its own request and its own state, so a card can only ever be
 * wrong about ITSELF, and a failed one says so (dash + retry) instead of reporting zero.
 *
 * `read` maps the rows to what the card shows, and is only ever given a real list: a
 * non-array 200 (the production proxy answers 200 + index.html for an unknown `/api`
 * path) is a FAILURE, because counting `0` from a body nobody could parse is the same
 * false claim as counting `0` from a request that never succeeded.
 *
 * `select` picks those rows out of the response body, and defaults to "the body IS the
 * list" — the shape five of these endpoints answer with. The inbox is the sixth: its list
 * endpoint answers an OBJECT (`{conversations, unread_conversations, cursor}`), because
 * the poll needs the server's cursor alongside the rows. Selecting is deliberately
 * separate from `read`: whatever `select` returns still has to be an array, so a malformed
 * body cannot slip past the guard by being mapped into a number. `read` also receives the
 * whole body, for a card whose hint depends on a field beside the rows.
 *
 * `params`, when present, is called at request time for the query string — the opening
 * hours are a RANGE read, and "the next seven days" must be measured when the card loads
 * (or is retried), not when this module was imported.
 */
const WHOLE_BODY = (data) => data;

const OPEN_HOURS_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Total length of `[{start, end}]` windows, in whole hours (rounded). */
const windowHours = (windows) => Math.round(windows.reduce(
  (sum, w) => sum + Math.max(0, new Date(w.end) - new Date(w.start)), 0) / (60 * 60 * 1000));
const CARD_SOURCES = [
  {
    key: "blog", url: "/admin/blog", icon: FileText, labelKey: "nav.blog",
    read: (rows) => ({ value: rows.length, hintCount: rows.filter((p) => p.published).length, hintKey: "overview.published" }),
  },
  {
    key: "testimonials", url: "/admin/testimonials", icon: Quote, labelKey: "nav.testimonials",
    read: (rows) => ({ value: rows.length, hintCount: rows.filter((r) => r.active).length, hintKey: "overview.active" }),
  },
  {
    key: "messages", url: "/admin/conversations", icon: MessageSquare, labelKey: "nav.messages",
    select: (data) => data?.conversations,
    // `practitioner_has_unread` is the server's own derivation (two seq integers
    // compared), never a flag this screen could recompute differently from the sidebar.
    read: (rows) => ({ value: rows.length, hintCount: rows.filter((c) => c.practitioner_has_unread).length, hintKey: "overview.unread" }),
  },
  {
    key: "bookings", url: "/admin/bookings", icon: CalendarDays, labelKey: "nav.bookings",
    read: (rows) => ({ value: rows.length, hintCount: rows.filter((b) => b.status === "pending").length, hintKey: "overview.pending" }),
  },
  {
    // The practice's opening hours over the next seven days (weekly windows and extra
    // hours, minus closures) — how much time clients can book into. A DRAFT schedule's
    // hours are counted but said to be unpublished: they are not bookable yet.
    key: "availability", url: "/admin/open-hours", icon: Clock, labelKey: "nav.availability",
    params: () => {
      const from = new Date();
      return { from: from.toISOString(), to: new Date(from.getTime() + OPEN_HOURS_DAYS * DAY_MS).toISOString() };
    },
    select: (data) => data?.windows,
    read: (rows, data) => ({
      value: windowHours(rows),
      hintKey: data?.status === "published" ? "overview.openHoursWeek" : "overview.openHoursDraft",
    }),
  },
  {
    key: "media", url: "/admin/media", icon: ImageIcon, labelKey: "nav.media",
    read: (rows) => ({ value: rows.length, hintKey: "overview.filesUploaded" }),
  },
];

/**
 * One summary card, in whichever of its three states it is in.
 *
 * The failed state shows a DASH, never a number: "0" is an answer, and this card has
 * none. The retry re-runs that card's request alone — the other five are not disturbed,
 * which is the entire point of the split above.
 */
const Card = ({ icon: Icon, testKey, label, state, hint, errorMessage, retryLabel, onRetry }) => (
  <div className="bg-eh-cream border border-eh-border-soft rounded-2xl p-6" data-testid={`overview-card-${testKey}`}>
    <div className="flex items-center gap-2 text-eh-sage-deep">
      <Icon size={16} />
      <div className="text-xs uppercase tracking-[0.18em]">{label}</div>
    </div>
    <div
      className={`font-display text-4xl mt-3 ${state.status === "ok" ? "text-eh-ink" : "text-eh-muted"}`}
      data-testid={`overview-card-${testKey}-value`}
    >
      {state.status === "ok" ? state.value : "—"}
    </div>
    {state.status === "error" ? (
      <div role="alert" data-testid={`overview-card-${testKey}-error`} className="mt-1">
        <p className="text-xs text-red-700 m-0">{errorMessage}</p>
        <button
          type="button"
          onClick={onRetry}
          data-testid={`overview-card-${testKey}-retry`}
          className="text-xs underline text-red-800 mt-1"
        >
          {retryLabel}
        </button>
      </div>
    ) : (
      state.status === "ok" && hint && <div className="text-xs text-eh-muted mt-1">{hint}</div>
    )}
  </div>
);

function SiteBuilderNudge({ tenant }) {
  const t = useT("admin");
  if (!tenant) return null;
  const published = !!(tenant.site_published || tenant.activated_at);

  // Site already live → offer to edit it (no "build it" nudge).
  if (published) {
    // `_SERVING_STATUSES` (tenant_middleware.py) excludes `suspended` unconditionally —
    // the public site really is down for every suspended tenant, whatever the reason
    // (non-payment or a platform operator's sanction alike). This card stated a plain
    // fact ("your site is live") next to AdminLayout's own suspension banner, which is
    // reason-gated for a different purpose (never sending a manually-sanctioned owner to
    // billing) — this card carries no such nuance, so it must not repeat the banner's
    // silence: a suspended site is offline, full stop.
    //
    // `past_due` keeps serving during its grace window (same set), so the headline stays
    // — it only grows a note, reusing the layout's own `acct.pastDue.text` rather than
    // inventing a second sentence for the same fact.
    const suspended = tenant.status === "suspended";
    const pastDue = tenant.status === "past_due";

    if (suspended) {
      return (
        <Link
          to={adminHref("/admin/billing")}
          data-testid="site-suspended-notice"
          className="flex items-center justify-between gap-4 bg-rose-50 border border-rose-200 rounded-2xl px-5 py-4 hover:shadow-sm transition group"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
              <OctagonAlert className="w-4 h-4 text-rose-700" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-rose-900">{t("bill.dunning.suspendedTitle")}</div>
              <div className="text-xs text-rose-800">{t("bill.dunning.suspendedBody")}</div>
            </div>
          </div>
          <span className="text-xs font-medium text-rose-900 whitespace-nowrap group-hover:translate-x-0.5 transition">
            {t("acct.billingCta")} →
          </span>
        </Link>
      );
    }

    return (
      <Link
        to={adminHref("/admin/site-builder")}
        data-testid="site-edit-nudge"
        className="flex items-center justify-between gap-4 bg-eh-cream border border-eh-border-soft rounded-2xl px-5 py-4 hover:shadow-sm transition group"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-eh-sage/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-eh-sage-deep" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-eh-ink">{t("overview.siteOnline")}</div>
            <div className="text-xs text-eh-muted">{t("overview.siteOnlineSub")}</div>
            {pastDue && (
              <div className="text-xs text-amber-700 mt-1">{t("acct.pastDue.text")}</div>
            )}
          </div>
        </div>
        <span className="text-xs font-medium text-sol-gold-deep whitespace-nowrap group-hover:translate-x-0.5 transition">
          {t("overview.editSite")} →
        </span>
      </Link>
    );
  }

  // Site not published → activation nudge.
  return (
    <Link
      to={adminHref("/admin/site-builder")}
      data-testid="site-builder-nudge"
      className="block bg-gradient-to-r from-sol-gold/15 via-amber-50 to-sol-gold/10 border border-sol-gold/40 rounded-3xl p-6 hover:shadow-md transition group"
    >
      <div className="flex items-center gap-5">
        <div className="w-14 h-14 rounded-2xl bg-sol-gold/20 flex items-center justify-center flex-shrink-0">
          <Rocket className="w-7 h-7 text-sol-gold-deep" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-wider font-bold text-sol-gold-deep bg-white/60 px-2 py-0.5 rounded-full">
              <Sparkles className="w-3 h-3 inline mr-0.5" /> {t("overview.notPublished")}
            </span>
          </div>
          <div className="font-display text-xl text-eh-ink">{t("overview.start5min")}</div>
          <div className="text-sm text-eh-muted mt-0.5">{t("overview.startSub")}</div>
        </div>
        <div className="text-sol-gold-deep group-hover:translate-x-1 transition text-2xl">→</div>
      </div>
    </Link>
  );
}

export default function Overview() {
  const t = useT("admin");
  const { granted } = useEntitlements();
  const tv = useT("common");
  const [tenant, setTenant] = useState(null);
  // key -> {status: "loading" | "ok" | "error", value?, hintCount?, hintKey?}
  const [cards, setCards] = useState(
    () => Object.fromEntries(CARD_SOURCES.map((s) => [s.key, { status: "loading" }])),
  );

  // Never rejects: one card's failure is recorded in that card's own slot and nowhere
  // else, so it cannot abort a sibling's request the way `Promise.all` did.
  const loadCard = useCallback(async (source) => {
    setCards((c) => ({ ...c, [source.key]: { status: "loading" } }));
    try {
      const { data } = source.params
        ? await api.get(source.url, { params: source.params() })
        : await api.get(source.url);
      const rows = (source.select || WHOLE_BODY)(data);
      if (!Array.isArray(rows)) throw payloadShapeError();
      setCards((c) => ({ ...c, [source.key]: { status: "ok", ...source.read(rows, data) } }));
    } catch (e) {
      setCards((c) => ({ ...c, [source.key]: { status: "error" } }));
      // Kept: a card that says "could not load" is for the practitioner, the stack is for
      // whoever they report it to.
      console.error(`Overview card ${source.key} failed to load`, e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      // The nudge is HIDDEN when this read fails (`SiteBuilderNudge` returns null without
      // a tenant), never guessed: telling a practitioner their site is unpublished when
      // nobody could read its status is the same class of lie as the zeros above.
      try {
        const { data } = await api.get("/tenant/self/info");
        setTenant(data);
      } catch (e) {
        console.error("Overview could not read the practice's own record", e);
      }
    })();
    CARD_SOURCES.forEach(loadCard);
  }, [loadCard]);

  return (
    <div data-testid="admin-overview" className="space-y-8">
      <div>
        <h1 className="font-display text-4xl text-eh-ink">{t("overview.hello")} 🌿</h1>
        <p className="text-eh-muted mt-2">{t("overview.subtitle")}</p>
      </div>

      <SiteBuilderNudge tenant={tenant} />

      <AcademyProUpsell />

      {/* The daily insights are the AI assistant's: the card, and the request it makes,
          exist only while that capability is usable. */}
      {granted(CAP.aiAssistant) && <AiInsightsCard />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {CARD_SOURCES.map((source) => {
          const state = cards[source.key];
          return (
            <Card
              key={source.key}
              testKey={source.key}
              icon={source.icon}
              label={t(source.labelKey)}
              state={state}
              hint={state.hintKey
                ? `${state.hintCount != null ? `${state.hintCount} ` : ""}${t(state.hintKey)}`
                : ""}
              errorMessage={tv("errors.loadFailedShort")}
              retryLabel={tv("actions.retry")}
              onRetry={() => loadCard(source)}
            />
          );
        })}
      </div>
    </div>
  );
}
